/**
 * EmailManagementModal Component
 *
 * Parent modal component providing tabbed interface for email management functions:
 * - Send: Bulk email sending interface for beta applications
 * - Templates: Email template management for all email types
 */

import React, { useState } from 'react';
import { Mail, Send, Settings } from 'lucide-react';

import {
  Modal,
  ModalContent,
  ModalBody,
  Card,
  CardHeader,
  CardBody,
  ButtonGroup
} from '@enfomentis/ui';

import { BulkEmail } from './BulkEmail';
import { TemplateEditing } from './TemplateEditing';

/**
 * Props for EmailManagementModal component
 */
export interface EmailManagementModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Function to close the modal */
  onClose: () => void;
  /** Optional initial tab to select */
  initialTab?: 'send' | 'templates';
}

/**
 * Email Management Modal
 *
 * Features:
 * - Tabbed interface for Send and Templates functionality
 * - Bulk email sending with applicant selection and progress tracking
 * - Email template management with preview and validation
 * - Mobile-responsive design with proper tab navigation
 * - Integration with existing email infrastructure
 *
 * Usage:
 * ```tsx
 * <EmailManagementModal
 *   isOpen={isModalOpen}
 *   onClose={closeModal}
 *   initialTab="send"
 * />
 * ```
 */
export const EmailManagementModal: React.FC<EmailManagementModalProps> = ({
  isOpen,
  onClose,
  initialTab = 'send'
}) => {
  // State management
  const [selectedTab, setSelectedTab] = useState<string>(initialTab);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="5xl"
      scrollBehavior="inside"
      closeButton
    >
      <ModalContent className="h-screen">
        {() => (
          <ModalBody className="p-0 flex-1">
            <Card shadow="none" radius="none" fullWidth className="h-full flex flex-col">
              {/* Header with Tab Selection */}
              <CardHeader className="flex-shrink-0 border-b border-default-200">
                <div className="w-full space-y-4">
                  <div className="flex items-center gap-2">
                    <Mail size={20} className="text-primary" />
                    <h2 className="text-lg font-semibold text-text-primary">
                      Email Management
                    </h2>
                  </div>

                  <ButtonGroup
                    options={[
                      { label: 'Delivery', value: 'send', icon: Send },
                      { label: 'Templates', value: 'templates', icon: Settings }
                    ]}
                    value={selectedTab}
                    onChange={(value) => setSelectedTab(value as string)}
                    variant="light"
                    color="default"
                    size="md"
                    radius="none"
                    fullWidth
                    underlined
                  />
                </div>
              </CardHeader>

              {/* Content */}
              <CardBody className="flex-1 p-0">
                {selectedTab === 'send' && <BulkEmail />}
                {selectedTab === 'templates' && <TemplateEditing />}
              </CardBody>
            </Card>
          </ModalBody>
        )}
      </ModalContent>
    </Modal>
  );
};